// Voice AI Pulse vs Project Health
//
// Each entry anchors to an existing project in `grants.js` so the aggregate
// project metrics stay consistent with the Projects Overview tables. The Voice
// AI side is a single beneficiary sample — its sentiment may or may not align
// with the aggregate project health, which is precisely the tension this widget
// surfaces.

import { GRANTS, enrichGrant, ragForScore } from "./grants";
import { PARTNERS } from "./partners";

const partnerKpi = (partnerId) =>
  PARTNERS.find((p) => p.id === partnerId)?.kpiCompletion ?? 70;

function deriveSignal(score, sentiment) {
  const strong = score >= 4.0;
  if (strong && sentiment === "positive")  return "aligned_positive";
  if (strong && sentiment === "negative")  return "early_warning";
  if (!strong && sentiment === "positive") return "resilience";
  if (!strong && sentiment === "negative") return "confirms_concern";
  return "neutral";
}

const RAW = [
  // 1 — Aligned positive (strong + positive)
  {
    id: "vp-1",
    grantId: "LEN-P-007",
    beneficiary: { name: "Lakshmi Iyer", initials: "LI", location: "Tamil Nadu · Madurai", capturedOn: "Oct 2024" },
    sentiment: "positive",
    excerpt:
      "Our village school had no science teacher. The online classes connected us to a real teacher — my marks jumped and I finally understand maths.",
    insight:
      "Aggregate metrics validated on the ground — the remote-teaching model is closing a genuine access gap.",
  },

  // 2 — Aligned positive (strong + positive)
  {
    id: "vp-2",
    grantId: "LEN-P-001",
    beneficiary: { name: "Arjun Gowda", initials: "AG", location: "Karnataka · Mysuru", capturedOn: "Jul 2024" },
    sentiment: "positive",
    excerpt:
      "The mobile science lab came to our school every week. Building experiments with my own hands made me fall in love with physics.",
    insight:
      "Strong project score corroborated by transformative learner experience and rising STEM aspiration.",
  },

  // 3 — Early-warning signal (strong + negative) — KEY DEMO MOMENT
  {
    id: "vp-3",
    grantId: "LEN-P-005",
    beneficiary: { name: "Sana Khan", initials: "SK", location: "Uttar Pradesh · Lucknow", capturedOn: "Aug 2024" },
    sentiment: "negative",
    excerpt:
      "The coding class is wonderful, but we have only one lab period a week and the internet keeps dropping. I forget what I learnt by the next session.",
    insight:
      "Project scoring 4.5/5 — yet Voice AI surfaced an infrastructure constraint surveys would miss. Flag connectivity & lab-time for partner follow-up.",
  },

  // 4 — Early-warning signal (strong + negative)
  {
    id: "vp-4",
    grantId: "LEN-P-010",
    beneficiary: { name: "Vignesh R.", initials: "VR", location: "Puducherry · Puducherry", capturedOn: "Sep 2024" },
    sentiment: "negative",
    excerpt:
      "Training was good and I got placed, but the factory is 40 km away with no transport. Some of my batchmates have already dropped out.",
    insight:
      "Strong placement outcomes mask a retention risk. Partner should review post-placement transport & support.",
  },

  // 5 — Resilience signal (at-risk + positive) — KEY DEMO MOMENT
  {
    id: "vp-5",
    grantId: "LEN-P-006",
    beneficiary: { name: "Heena Patel", initials: "HP", location: "Gujarat · Ahmedabad", capturedOn: "Oct 2024" },
    sentiment: "positive",
    excerpt:
      "Sharing one tablet between four of us was hard, but my teacher made a roster. I was first in my family to clear Class 10 with distinction.",
    insight:
      "Project score is at-risk (2.9/5) but committed teaching is producing standout outcomes. Worth fixing the device-to-student ratio to scale impact.",
  },

  // 6 — Confirms concern (at-risk + negative)
  {
    id: "vp-6",
    grantId: "LEN-P-014",
    beneficiary: { name: "Mahesh Pawar", initials: "MP", location: "Maharashtra · Pune", capturedOn: "Aug 2024" },
    sentiment: "negative",
    excerpt:
      "The e-waste bins were placed, but no one explained how to use them. Most of us still throw old chargers in regular trash.",
    insight:
      "At-risk score corroborated by ground-level adoption failure. Awareness drives and collection logistics need urgent redesign.",
  },
];

export const VOICE_VS_PROJECT = RAW.map((item) => {
  const g = enrichGrant(GRANTS.find((x) => x.id === item.grantId));
  return {
    ...item,
    project: {
      id: g.id,
      name: g.name,
      partner: g.partnerName,
      partnerId: g.partnerId,
      pillar: g.pillar,
      score: g.score,
      scoreRag: ragForScore(g.score),
      status: g.status,
      sanctionedCr: g.fundedCr,
      utilisedCr: g.utilisedCr,
      utilisePct: g.utilisePct,
      kpiCompletion: partnerKpi(g.partnerId),
    },
    signal: deriveSignal(g.score, item.sentiment),
  };
});

export const SENTIMENT_META = {
  positive: { label: "Positive", tone: "green", emoji: "👍", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "#16A34A" },
  neutral:  { label: "Neutral",  tone: "slate", emoji: "😐", chip: "bg-slate-50  text-slate-700  border-slate-200",   dot: "#64748B" },
  negative: { label: "Negative", tone: "rose",  emoji: "👎", chip: "bg-rose-50   text-rose-700   border-rose-200",    dot: "#DC2626" },
};

export const SIGNAL_META = {
  aligned_positive:  { label: "Aggregate validated",  tone: "green",  fill: "#16A34A", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  early_warning:     { label: "Early-warning signal", tone: "amber",  fill: "#D97706", chip: "bg-amber-50 text-amber-700 border-amber-200" },
  resilience:        { label: "Resilience signal",    tone: "blue",   fill: "#2563EB", chip: "bg-blue-50 text-blue-700 border-blue-200" },
  confirms_concern:  { label: "Confirms concern",     tone: "rose",   fill: "#DC2626", chip: "bg-rose-50 text-rose-700 border-rose-200" },
  neutral:           { label: "Neutral signal",       tone: "slate",  fill: "#64748B", chip: "bg-slate-50 text-slate-700 border-slate-200" },
};
